// ── Inspector Panel ──────────────────────────────────────────
// Deep node editing panel with full metadata control.
// Slide-in from right, shows: editable label/reasoning, type selector,
// parent links, children list, scores, timestamps, and raw data view.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './InspectorPanel.css';

const TYPE_OPTIONS = [
  'thesis', 'hypothesis', 'antithesis', 'synthesis', 'evidence',
  'assumption', 'implication', 'question', 'constraint', 'analogy',
  'experiment', 'refinement', 'component', 'risk',
];

const TYPE_COLORS = {
  thesis: '#a78bfa', hypothesis: '#818cf8', antithesis: '#f472b6',
  synthesis: '#34d399', evidence: '#60a5fa', assumption: '#fbbf24',
  implication: '#f97316', question: '#e879f9', constraint: '#94a3b8',
  analogy: '#2dd4bf', experiment: '#a3e635', refinement: '#c084fc',
  component: '#38bdf8', risk: '#fb7185',
};

function getParentIds(n) {
  return n.data?.parentIds || (n.data?.parentId ? [n.data.parentId] : []);
}

const SYNAPSE_TYPES = ['excitatory', 'inhibitory', 'modulatory', 'dependency', 'analogy'];
const SYNAPSE_COLORS = {
  excitatory: '#22c55e', inhibitory: '#ef4444', modulatory: '#a855f7',
  dependency: '#f97316', analogy: '#3b82f6',
};
const REACTION_OPTIONS = ['notify', 'expand', 're-evaluate', 'webhook_out', 'cascade'];

export default function InspectorPanel({
  node,           // the full node object
  rawNodesRef,    // for parent/child lookups
  onSave,         // (nodeId, updates) => void
  onClose,        // close panel
  onNodeClick,    // navigate to a node
  streamActions,  // { attachStream, removeStream, pauseStream, resumeStream, setConfidence } from useStreams
  synapseActions, // { createSynapse, updateSynapse, deleteSynapse, getNodeSynapses } from useSynapses
  activationActions, // { setActivation } from useActivation
}) {
  const [editLabel, setEditLabel] = useState('');
  const [editReasoning, setEditReasoning] = useState('');
  const [editType, setEditType] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Stream form state
  const [showAddStream, setShowAddStream] = useState(false);
  const [streamType, setStreamType] = useState('url');
  const [streamUrl, setStreamUrl] = useState('');
  const [streamInterval, setStreamInterval] = useState('1h');
  const [streamLoading, setStreamLoading] = useState(false);
  const [showSignalLog, setShowSignalLog] = useState(false);
  // Synapse form state
  const [showAddSynapse, setShowAddSynapse] = useState(false);
  const [synTarget, setSynTarget] = useState('');
  const [synType, setSynType] = useState('excitatory');
  const [synWeight, setSynWeight] = useState(0.5);
  const [synLabel, setSynLabel] = useState('');
  const [synLoading, setSynLoading] = useState(false);
  // Activation form state
  const [showActivation, setShowActivation] = useState(false);
  const [actSignalCount, setActSignalCount] = useState(3);
  const [actConfDelta, setActConfDelta] = useState(0.2);
  const [actReactions, setActReactions] = useState(['notify']);
  const [actRefractory, setActRefractory] = useState('1h');
  const [actLoading, setActLoading] = useState(false);

  // Populate fields when node changes
  useEffect(() => {
    if (!node) return;
    setEditLabel(node.data?.label || '');
    setEditReasoning(node.data?.reasoning || '');
    setEditType(node.data?.type || '');
    setDirty(false);
  }, [node]);

  const handleFieldChange = useCallback((setter) => (e) => {
    setter(e.target.value);
    setDirty(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!node || !dirty) return;
    onSave(node.id, {
      label: editLabel,
      reasoning: editReasoning,
      type: editType,
    });
    setDirty(false);
  }, [node, dirty, editLabel, editReasoning, editType, onSave]);

  // Parent and child info
  const { parents, children } = useMemo(() => {
    if (!node || !rawNodesRef?.current) return { parents: [], children: [] };
    const nodes = rawNodesRef.current;
    const parentIds = getParentIds(node);
    const parents = parentIds
      .map(pid => nodes.find(n => n.id === pid))
      .filter(Boolean);
    const children = nodes.filter(n => {
      const pids = getParentIds(n);
      return pids.includes(node.id);
    });
    return { parents, children };
  }, [node, rawNodesRef]);

  // Keyboard: Esc to close, Ctrl+S to save
  useEffect(() => {
    function handler(e) {
      if (e.key === 'Escape') { onClose(); return; }
      const mod = navigator.platform?.includes('Mac') ? e.metaKey : e.ctrlKey;
      if (mod && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, handleSave]);

  if (!node) return null;
  const d = node.data || {};
  const color = TYPE_COLORS[d.type] || '#888';

  return (
    <div className="inspector-panel">
      {/* Header */}
      <div className="inspector-header">
        <div className="inspector-title-row">
          <div className="inspector-dot" style={{ background: color }} />
          <span className="inspector-title">Inspector</span>
          <span className="inspector-node-id">{node.id?.slice(0, 8)}…</span>
        </div>
        <button className="inspector-close" onClick={onClose}>✕</button>
      </div>

      <div className="inspector-body">
        {/* Type selector */}
        <div className="inspector-section">
          <label className="inspector-label">Type</label>
          <div className="inspector-type-grid">
            {TYPE_OPTIONS.map(t => (
              <button
                key={t}
                className={`inspector-type-chip ${editType === t ? 'active' : ''}`}
                style={{
                  borderColor: editType === t ? (TYPE_COLORS[t] || '#888') : 'transparent',
                  color: editType === t ? (TYPE_COLORS[t] || '#888') : '#666',
                }}
                onClick={() => { setEditType(t); setDirty(true); }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Label */}
        <div className="inspector-section">
          <label className="inspector-label">Label</label>
          <textarea
            className="inspector-textarea"
            value={editLabel}
            onChange={handleFieldChange(setEditLabel)}
            rows={2}
            placeholder="Node label…"
          />
        </div>

        {/* Reasoning */}
        <div className="inspector-section">
          <label className="inspector-label">Reasoning</label>
          <textarea
            className="inspector-textarea inspector-reasoning"
            value={editReasoning}
            onChange={handleFieldChange(setEditReasoning)}
            rows={4}
            placeholder="Reasoning / notes…"
          />
        </div>

        {/* Metadata badges */}
        <div className="inspector-section">
          <label className="inspector-label">Metadata</label>
          <div className="inspector-meta-row">
            {d.starred && <span className="inspector-badge badge-star">★ Starred</span>}
            {d.score != null && (
              <span className={`inspector-badge ${d.score >= 8 ? 'badge-good' : d.score >= 5 ? 'badge-mid' : 'badge-low'}`}>
                Score: {d.score}/10
              </span>
            )}
            {d.lens && <span className="inspector-badge badge-lens">{d.lens}</span>}
            {d.depth > 0 && <span className="inspector-badge badge-depth">Depth {d.depth}</span>}
          </div>
        </div>

        {/* Confidence */}
        <div className="inspector-section">
          <label className="inspector-label">Confidence</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="range"
              min="0" max="100" step="1"
              value={Math.round((d.confidence ?? 0.7) * 100)}
              onChange={(e) => {
                const val = parseInt(e.target.value) / 100;
                streamActions?.setConfidence?.(node.id, val);
              }}
              style={{ flex: 1, accentColor: (d.confidence ?? 0.7) > 0.6 ? '#22c55e' : (d.confidence ?? 0.7) > 0.4 ? '#facc15' : '#f87171' }}
            />
            <span className="inspector-badge" style={{
              background: (d.confidence ?? 0.7) > 0.6 ? 'rgba(34,197,94,0.15)' : (d.confidence ?? 0.7) > 0.4 ? 'rgba(250,204,21,0.15)' : 'rgba(248,113,113,0.15)',
              color: (d.confidence ?? 0.7) > 0.6 ? '#22c55e' : (d.confidence ?? 0.7) > 0.4 ? '#facc15' : '#f87171',
              minWidth: 40, textAlign: 'center',
            }}>
              {((d.confidence ?? 0.7) * 100).toFixed(0)}%
            </span>
          </div>
        </div>

        {/* Data Streams */}
        <div className="inspector-section">
          <label className="inspector-label">
            Data Streams ({(d.streams || []).length}/3)
          </label>

          {/* Existing streams */}
          {(d.streams || []).map(s => (
            <div key={s.id} className="inspector-meta-row" style={{ marginBottom: 4, alignItems: 'center' }}>
              <span className="inspector-badge" style={{
                background: s.status === 'active' ? 'rgba(34,197,94,0.15)' : s.status === 'paused' ? 'rgba(250,204,21,0.15)' : 'rgba(248,113,113,0.15)',
                color: s.status === 'active' ? '#22c55e' : s.status === 'paused' ? '#facc15' : '#f87171',
                fontSize: 9,
              }}>
                {s.type.toUpperCase()} {s.status}
              </span>
              <span style={{ fontSize: 10, color: '#666', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginLeft: 4 }}>
                {s.url || s.webhookUrl || ''}
              </span>
              <span style={{ fontSize: 9, color: '#555', marginLeft: 4 }}>{s.interval}</span>
              <button
                className="inspector-raw-toggle"
                style={{ marginLeft: 4, fontSize: 9, padding: '0 4px' }}
                onClick={() => {
                  if (s.status === 'active') streamActions?.pauseStream?.(node.id, s.id);
                  else streamActions?.resumeStream?.(node.id, s.id);
                }}
              >
                {s.status === 'active' ? '⏸' : '▶'}
              </button>
              <button
                className="inspector-raw-toggle"
                style={{ marginLeft: 2, fontSize: 9, padding: '0 4px', color: '#f87171' }}
                onClick={() => streamActions?.removeStream?.(node.id, s.id)}
              >
                ✕
              </button>
            </div>
          ))}

          {/* Add stream form */}
          {(d.streams || []).length < 3 && !showAddStream && (
            <button
              className="inspector-raw-toggle"
              style={{ fontSize: 10, marginTop: 4 }}
              onClick={() => setShowAddStream(true)}
            >
              + Add stream
            </button>
          )}
          {showAddStream && (
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {['url', 'rss', 'webhook'].map(t => (
                  <button
                    key={t}
                    className={`inspector-type-chip ${streamType === t ? 'active' : ''}`}
                    style={{ borderColor: streamType === t ? '#38bdf8' : 'transparent', color: streamType === t ? '#38bdf8' : '#666', fontSize: 9 }}
                    onClick={() => setStreamType(t)}
                  >
                    {t.toUpperCase()}
                  </button>
                ))}
              </div>
              {streamType !== 'webhook' && (
                <input
                  className="inspector-textarea"
                  style={{ height: 28, fontSize: 10, padding: '4px 8px' }}
                  placeholder={streamType === 'rss' ? 'RSS feed URL…' : 'URL to monitor…'}
                  value={streamUrl}
                  onChange={e => setStreamUrl(e.target.value)}
                />
              )}
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {streamType !== 'webhook' && (
                  <select
                    value={streamInterval}
                    onChange={e => setStreamInterval(e.target.value)}
                    style={{ background: '#1a1a2e', color: '#aaa', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, fontSize: 10, padding: '2px 4px' }}
                  >
                    <option value="15m">15m</option>
                    <option value="30m">30m</option>
                    <option value="1h">1h</option>
                    <option value="6h">6h</option>
                    <option value="12h">12h</option>
                    <option value="1d">1d</option>
                  </select>
                )}
                <button
                  className="inspector-save-btn"
                  style={{ fontSize: 10, padding: '3px 10px', flex: 1 }}
                  disabled={streamLoading || (streamType !== 'webhook' && !streamUrl)}
                  onClick={async () => {
                    setStreamLoading(true);
                    try {
                      await streamActions?.attachStream?.(node.id, {
                        type: streamType,
                        url: streamType !== 'webhook' ? streamUrl : undefined,
                        interval: streamType !== 'webhook' ? streamInterval : undefined,
                      });
                      setShowAddStream(false);
                      setStreamUrl('');
                    } catch (err) {
                      alert(err.message);
                    } finally {
                      setStreamLoading(false);
                    }
                  }}
                >
                  {streamLoading ? 'Attaching…' : 'Attach'}
                </button>
                <button
                  className="inspector-raw-toggle"
                  style={{ fontSize: 10 }}
                  onClick={() => { setShowAddStream(false); setStreamUrl(''); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Signal Log */}
        <div className="inspector-section">
          <button className="inspector-raw-toggle" onClick={() => setShowSignalLog(!showSignalLog)}>
            {showSignalLog ? '▾' : '▸'} Signal Log ({(d.signals || []).length})
          </button>
          {showSignalLog && (
            <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 4 }}>
              {(d.signals || []).length === 0 ? (
                <span style={{ fontSize: 10, color: '#555' }}>No signals yet</span>
              ) : (
                [...(d.signals || [])].reverse().map(sig => (
                  <div key={sig.id} style={{
                    padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.04)',
                    fontSize: 10, color: '#aaa',
                  }}>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <span style={{
                        width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                        background: sig.impact === 'reinforces' ? '#22c55e' : sig.impact === 'contradicts' ? '#f87171' : '#666',
                      }} />
                      <span style={{ flex: 1 }}>{sig.summary || 'Signal'}</span>
                      <span style={{ fontSize: 8, color: '#555' }}>
                        {sig.timestamp ? new Date(sig.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    {sig.delta && <div style={{ fontSize: 9, color: '#666', marginLeft: 10, marginTop: 1 }}>{sig.delta}</div>}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Synapses */}
        <div className="inspector-section">
          <label className="inspector-label">
            Synapses ({((d.synapses?.incoming || []).length + (d.synapses?.outgoing || []).length)})
          </label>

          {/* Outgoing synapses */}
          {(d.synapses?.outgoing || []).map(s => {
            const targetNode = rawNodesRef?.current?.find(n => n.id === s.target);
            const tLabel = targetNode?.data?.label?.slice(0, 30) || s.target?.slice(0, 8);
            return (
              <div key={s.id} className="inspector-meta-row" style={{ marginBottom: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 9, color: SYNAPSE_COLORS[s.type] || '#888' }}>→</span>
                <span className="inspector-badge" style={{
                  background: `${SYNAPSE_COLORS[s.type] || '#888'}22`,
                  color: SYNAPSE_COLORS[s.type] || '#888',
                  fontSize: 9,
                }}>
                  {s.type}
                </span>
                <button
                  className="inspector-node-link"
                  style={{ fontSize: 10, flex: 1, textAlign: 'left', padding: '0 4px' }}
                  onClick={() => { if (targetNode) onNodeClick?.(targetNode); }}
                >
                  {tLabel}
                </button>
                <span style={{ fontSize: 9, color: '#666' }}>w:{(s.weight || 0.5).toFixed(1)}</span>
                <button
                  className="inspector-raw-toggle"
                  style={{ marginLeft: 4, fontSize: 9, padding: '0 4px', color: '#f87171' }}
                  onClick={() => synapseActions?.deleteSynapse?.(s.id)}
                >
                  ✕
                </button>
              </div>
            );
          })}

          {/* Incoming synapses */}
          {(d.synapses?.incoming || []).map(s => {
            const sourceNode = rawNodesRef?.current?.find(n => n.id === s.source);
            const sLabel = sourceNode?.data?.label?.slice(0, 30) || s.source?.slice(0, 8);
            return (
              <div key={s.id} className="inspector-meta-row" style={{ marginBottom: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 9, color: SYNAPSE_COLORS[s.type] || '#888' }}>←</span>
                <span className="inspector-badge" style={{
                  background: `${SYNAPSE_COLORS[s.type] || '#888'}22`,
                  color: SYNAPSE_COLORS[s.type] || '#888',
                  fontSize: 9,
                }}>
                  {s.type}
                </span>
                <button
                  className="inspector-node-link"
                  style={{ fontSize: 10, flex: 1, textAlign: 'left', padding: '0 4px' }}
                  onClick={() => { if (sourceNode) onNodeClick?.(sourceNode); }}
                >
                  {sLabel}
                </button>
                <span style={{ fontSize: 9, color: '#666' }}>w:{(s.weight || 0.5).toFixed(1)}</span>
              </div>
            );
          })}

          {/* Add synapse form */}
          {!showAddSynapse && (
            <button
              className="inspector-raw-toggle"
              style={{ fontSize: 10, marginTop: 4 }}
              onClick={() => setShowAddSynapse(true)}
            >
              + Add synapse
            </button>
          )}
          {showAddSynapse && (
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {SYNAPSE_TYPES.map(t => (
                  <button
                    key={t}
                    className={`inspector-type-chip ${synType === t ? 'active' : ''}`}
                    style={{ borderColor: synType === t ? SYNAPSE_COLORS[t] : 'transparent', color: synType === t ? SYNAPSE_COLORS[t] : '#666', fontSize: 9 }}
                    onClick={() => setSynType(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <select
                value={synTarget}
                onChange={e => setSynTarget(e.target.value)}
                style={{ background: '#1a1a2e', color: '#aaa', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, fontSize: 10, padding: '4px 6px' }}
              >
                <option value="">Select target node…</option>
                {(rawNodesRef?.current || [])
                  .filter(n => n.id !== node.id)
                  .slice(0, 50)
                  .map(n => (
                    <option key={n.id} value={n.id}>{(n.data?.label || n.id).slice(0, 40)}</option>
                  ))
                }
              </select>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <label style={{ fontSize: 9, color: '#666' }}>Weight</label>
                <input type="range" min="0" max="100" step="5"
                  value={Math.round(synWeight * 100)}
                  onChange={e => setSynWeight(parseInt(e.target.value) / 100)}
                  style={{ flex: 1, accentColor: SYNAPSE_COLORS[synType] }}
                />
                <span style={{ fontSize: 9, color: '#aaa', minWidth: 24 }}>{synWeight.toFixed(1)}</span>
              </div>
              <input
                className="inspector-textarea"
                style={{ height: 28, fontSize: 10, padding: '4px 8px' }}
                placeholder="Label (optional)…"
                value={synLabel}
                onChange={e => setSynLabel(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  className="inspector-save-btn"
                  style={{ fontSize: 10, padding: '3px 10px', flex: 1 }}
                  disabled={synLoading || !synTarget}
                  onClick={async () => {
                    setSynLoading(true);
                    try {
                      await synapseActions?.createSynapse?.({
                        source: node.id, target: synTarget, type: synType,
                        weight: synWeight, label: synLabel || undefined,
                      });
                      setShowAddSynapse(false);
                      setSynTarget(''); setSynLabel('');
                    } catch (err) { alert(err.message); }
                    finally { setSynLoading(false); }
                  }}
                >
                  {synLoading ? 'Creating…' : 'Create'}
                </button>
                <button className="inspector-raw-toggle" style={{ fontSize: 10 }} onClick={() => { setShowAddSynapse(false); setSynTarget(''); setSynLabel(''); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Activation */}
        <div className="inspector-section">
          <button className="inspector-raw-toggle" onClick={() => setShowActivation(!showActivation)}>
            {showActivation ? '▾' : '▸'} Activation {d.activation?.reactions?.length ? `(${d.activation.reactions.join(', ')})` : '(not configured)'}
          </button>
          {showActivation && (
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div>
                <label style={{ fontSize: 9, color: '#666', display: 'block', marginBottom: 2 }}>Fire after N signals</label>
                <input type="number" min="1" max="50"
                  value={actSignalCount}
                  onChange={e => setActSignalCount(parseInt(e.target.value) || 3)}
                  style={{ background: '#1a1a2e', color: '#aaa', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, fontSize: 10, padding: '4px 6px', width: 60 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 9, color: '#666', display: 'block', marginBottom: 2 }}>Or when confidence changes by</label>
                <input type="number" min="0.05" max="1" step="0.05"
                  value={actConfDelta}
                  onChange={e => setActConfDelta(parseFloat(e.target.value) || 0.2)}
                  style={{ background: '#1a1a2e', color: '#aaa', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, fontSize: 10, padding: '4px 6px', width: 60 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 9, color: '#666', display: 'block', marginBottom: 2 }}>Reactions</label>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {REACTION_OPTIONS.map(r => (
                    <button
                      key={r}
                      className={`inspector-type-chip ${actReactions.includes(r) ? 'active' : ''}`}
                      style={{
                        borderColor: actReactions.includes(r) ? '#38bdf8' : 'transparent',
                        color: actReactions.includes(r) ? '#38bdf8' : '#666',
                        fontSize: 9,
                      }}
                      onClick={() => {
                        setActReactions(prev =>
                          prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]
                        );
                      }}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <label style={{ fontSize: 9, color: '#666' }}>Refractory</label>
                <select
                  value={actRefractory}
                  onChange={e => setActRefractory(e.target.value)}
                  style={{ background: '#1a1a2e', color: '#aaa', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, fontSize: 10, padding: '2px 4px' }}
                >
                  <option value="15m">15m</option>
                  <option value="30m">30m</option>
                  <option value="1h">1h</option>
                  <option value="4h">4h</option>
                  <option value="12h">12h</option>
                  <option value="1d">1d</option>
                </select>
              </div>
              <button
                className="inspector-save-btn"
                style={{ fontSize: 10, padding: '3px 10px' }}
                disabled={actLoading || actReactions.length === 0}
                onClick={async () => {
                  setActLoading(true);
                  try {
                    await activationActions?.setActivation?.(node.id, {
                      threshold: { signal_count: actSignalCount, confidence_delta: actConfDelta },
                      reactions: actReactions,
                      refractoryPeriod: actRefractory,
                    });
                  } catch (err) { alert(err.message); }
                  finally { setActLoading(false); }
                }}
              >
                {actLoading ? 'Saving…' : d.activation?.reactions?.length ? 'Update Activation' : 'Enable Activation'}
              </button>
              {d.activation?.lastFired && (
                <span style={{ fontSize: 9, color: '#555' }}>
                  Last fired: {new Date(d.activation.lastFired).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Parents */}
        {parents.length > 0 && (
          <div className="inspector-section">
            <label className="inspector-label">Parents ({parents.length})</label>
            <div className="inspector-node-list">
              {parents.map(p => (
                <button
                  key={p.id}
                  className="inspector-node-link"
                  onClick={() => onNodeClick?.(p)}
                >
                  <span className="inspector-link-dot" style={{ background: TYPE_COLORS[p.data?.type] || '#888' }} />
                  <span className="inspector-link-label">{p.data?.label?.slice(0, 40) || p.id}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Children */}
        {children.length > 0 && (
          <div className="inspector-section">
            <label className="inspector-label">Children ({children.length})</label>
            <div className="inspector-node-list">
              {children.slice(0, 10).map(c => (
                <button
                  key={c.id}
                  className="inspector-node-link"
                  onClick={() => onNodeClick?.(c)}
                >
                  <span className="inspector-link-dot" style={{ background: TYPE_COLORS[c.data?.type] || '#888' }} />
                  <span className="inspector-link-label">{c.data?.label?.slice(0, 40) || c.id}</span>
                </button>
              ))}
              {children.length > 10 && (
                <span className="inspector-more">+{children.length - 10} more</span>
              )}
            </div>
          </div>
        )}

        {/* Provenance */}
        {(d.generatedBy || d.generatedAt || d.chaosLevel != null || d.appliedConstraints?.length > 0) && (
          <div className="inspector-section">
            <label className="inspector-label">Provenance</label>
            <div className="inspector-meta-row" style={{ flexWrap: 'wrap', gap: '4px' }}>
              {d.generatedBy && (
                <span className="inspector-badge badge-lens">
                  {d.generatedBy === 'tree-generation' ? 'Tree Generation' :
                   d.generatedBy === 'fractal-expansion' ? 'Fractal Expansion' :
                   d.generatedBy}
                </span>
              )}
              {d.chaosLevel != null && (
                <span className={`inspector-badge ${d.chaosLevel <= 30 ? 'badge-low' : d.chaosLevel <= 70 ? 'badge-mid' : 'badge-good'}`}>
                  {d.chaosLevel <= 20 ? '◎ Conservative' :
                   d.chaosLevel <= 40 ? '◎ Moderate' :
                   d.chaosLevel <= 60 ? '◈ Balanced' :
                   d.chaosLevel <= 80 ? '✦ Exploratory' :
                   '✦ Wild'} ({d.chaosLevel}%)
                </span>
              )}
              {d.generatedAt && (
                <span className="inspector-badge badge-depth" title={d.generatedAt}>
                  {new Date(d.generatedAt).toLocaleDateString()}
                </span>
              )}
            </div>
            {d.appliedConstraints?.length > 0 && (
              <div className="inspector-meta-row" style={{ flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                {d.appliedConstraints.map((c, i) => (
                  <span key={i} className="inspector-badge badge-star" style={{ background: 'rgba(251, 191, 36, 0.12)', color: '#fbbf24', fontSize: '10px' }}>
                    ⊘ {c}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Raw JSON toggle */}
        <div className="inspector-section">
          <button className="inspector-raw-toggle" onClick={() => setShowRaw(!showRaw)}>
            {showRaw ? '▾' : '▸'} Raw Data
          </button>
          {showRaw && (
            <pre className="inspector-raw-json">
              {JSON.stringify(d, null, 2)}
            </pre>
          )}
        </div>
      </div>

      {/* Footer save bar */}
      {dirty && (
        <div className="inspector-footer">
          <button className="inspector-save-btn" onClick={handleSave}>
            Save Changes
          </button>
          <span className="inspector-save-hint">⌘S</span>
        </div>
      )}
    </div>
  );
}
